/**
 * <hl-bundle> - mix and match bundle selector.
 *
 * This lives in an asset and registers a custom element rather than sitting
 * inline in the snippet, because Horizon's quick add modal builds its content
 * with cloneNode + morph. Cloned nodes never execute inline <script>, so the
 * inline version rendered dead controls inside the modal: markup present, no
 * price, add to cart did nothing. Custom elements upgrade themselves whenever
 * they are inserted, so the same markup works on the PDP, in the quick add
 * modal, and anywhere else it gets injected later.
 *
 * Everything is scoped to the element, never to document, so two instances
 * (PDP behind an open modal, for example) cannot fight over the same controls.
 */
class HlBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hlReady) return;      // morph can re-attach the same node
    this.dataset.hlReady = '1';

    const cfgEl = this.querySelector('[data-hl-config]');
    let cfg = {};
    try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) {}
    this.PRICES = cfg.prices || {};
    this.CUR = cfg.cur || 'ZAR';
    this.FALLBACK = String(cfg.fallback || '');
    this.MYST_PRICE = Number(cfg.myst || 0);

    this.tiers = Array.prototype.slice.call(this.querySelectorAll('.hl-tier'));
    this.btn = this.querySelector('[data-hl-atc]');
    this.priceEl = this.querySelector('.hl-atc__price');
    this.errEl = this.querySelector('[data-hl-err]');
    this.myst = this.querySelector('[data-hl-myst]');
    if (!this.btn) return;

    this.addEventListener('change', (e) => {
      const sl = e.target.closest && e.target.closest('.hl-slot');
      if (sl) sl.dataset.touched = '1';
      this.paint();
    });

    // The native variant picker lives outside this element. Scope the listener
    // to the same container so a modal's picker does not drive the PDP bundle.
    this.scopeEl = this.closest('#quick-add-modal-content, [data-product-grid-content]') || document;
    this.scopeEl.addEventListener('change', (e) => {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-variant-id')) {
        this.seed(); this.paint();
      }
    }, true);

    this.btn.addEventListener('click', () => this.add());
    this.seed();
    this.paint();
  }

  fmt(c) {
    // Locale comes from the MARKET, not the browser. With undefined the browser
    // decides, so a Dutch or German visitor to the AU store saw "AU$ 88,50"
    // while the cart and checkout showed "$88.50". Same number, two formats,
    // on the same screen.
    var loc = this.CUR === 'ZAR' ? 'en-ZA' : (this.CUR === 'NZD' ? 'en-NZ' : 'en-AU');
    try { return new Intl.NumberFormat(loc, { style: 'currency', currency: this.CUR }).format(c / 100); }
    catch (e) { return (this.CUR === 'ZAR' ? 'R' : '$') + (c / 100).toFixed(2); }
  }

  tier() {
    const c = this.querySelector('input[name="hl-tier"]:checked');
    return c ? +c.value : 1;
  }

  activeTier() {
    return this.tiers.filter((t) => +t.dataset.tier === this.tier())[0];
  }

  nativeId() {
    const r = this.scopeEl.querySelector
      ? this.scopeEl.querySelector('input[data-variant-id]:checked')
      : null;
    if (r && r.dataset.variantId) return r.dataset.variantId;
    const u = new URLSearchParams(location.search).get('variant');
    return u || this.FALLBACK;
  }

  chosen() {
    const t = this.activeTier();
    const slots = t ? Array.prototype.slice.call(t.querySelectorAll('.hl-slot')) : [];
    if (!slots.length) return [this.nativeId()];
    return slots.map((sl) => {
      const sel = sl.querySelector('.hl-slot__sel');
      return sel ? sel.value : this.nativeId();
    });
  }

  total(ids) {
    // "Buy one get one free", scoped to the four frames, usesPerOrderLimit 1.
    // So EXACTLY ONE pair is free per order, and Shopify discounts the cheapest
    // eligible line. Not "every second pair": a third pair is full price.
    const prices = ids.map((id) => this.PRICES[id] || 0).sort((a, b) => a - b);
    let sum = prices.reduce((s, p) => s + p, 0);
    if (prices.length >= 2) sum -= prices[0];
    // The Mystery Pair is outside the discount, so it is never the free item
    // and never discounted. Add it at full price after the offer is applied.
    if (this.myst && this.myst.checked) sum += this.MYST_PRICE;
    return sum;
  }

  thumbs() {
    Array.prototype.slice.call(this.querySelectorAll('.hl-slot')).forEach((sl) => {
      const sel = sl.querySelector('.hl-slot__sel'); if (!sel) return;
      const img = sl.querySelector('.hl-slot__thumb');
      const opt = sel.selectedOptions[0];
      if (img && opt) { img.src = opt.dataset.img || ''; img.alt = opt.text; }
    });
  }

  paint() {
    const t = this.tier();
    this.tiers.forEach((el) => el.classList.toggle('is-on', +el.dataset.tier === t));
    this.thumbs();
    if (this.priceEl) this.priceEl.textContent = this.fmt(this.total(this.chosen()));
  }

  seed() {
    const id = this.nativeId();
    this.tiers.forEach((el) => {
      const slot = el.querySelector('.hl-slot[data-pair="1"]');
      if (!slot || slot.dataset.touched) return;
      const sel = slot.querySelector('.hl-slot__sel');
      if (sel) { const o = sel.querySelector('option[value="' + id + '"]'); if (o && !o.disabled) sel.value = id; }
    });
  }

  /**
   * Horizon's bag count only moves when the cartLinesUpdate event carries a
   * `promise` (cart-icon.js does `event.promise?.then(...)`). What is
   * dispatched above is a plain CustomEvent with no promise, so every listener
   * that reads the resolved cart was a no-op and the count stayed on whatever
   * the page was rendered with until a full reload. Rather than fake the event
   * contract, which would also reach the drawer and cart-items handlers, call
   * the icon's own render method: it owns the count text, the hidden classes,
   * the has-cart donut mask and the pop animation.
   *
   * Guarded by typeof, so a future Horizon release that renames the method
   * degrades to a stale count rather than throwing inside the add flow.
   */
  syncCartIcon() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => {
        document.querySelectorAll('cart-icon').forEach((icon) => {
          if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(c.item_count);
        });
        return c;
      })
      .catch(() => {});
  }

  /**
   * The drawer is rendered server side at page load, so opening it straight
   * after an AJAX add shows stale content, usually the empty state. Re-fetch
   * the section, swap it, then open. The event still fires so the cart count
   * and any other listeners update.
   */
  openDrawer() {
    const cd = document.querySelector('cart-drawer-component');
    const td = cd ? cd.closest('theme-drawer') : null;
    const lab = this.querySelector('.hl-atc__label');
    if (!cd || !td || typeof td.open !== 'function') { location.href = '/cart'; return; }

    try {
      const ev = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      ev.action = 'add'; ev.context = 'product';
      document.dispatchEvent(ev);
    } catch (e) {}

    this.syncCartIcon();

    return fetch('/?section_id=cart-drawer-section')
      .then((r) => { if (!r.ok) throw 0; return r.text(); })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('cart-drawer-component');
        if (fresh) cd.innerHTML = fresh.innerHTML;
        this.btn.disabled = false;
        if (lab) lab.textContent = 'Add to cart';
        // close the quick add modal, otherwise it covers the drawer we opened
        const dlg = document.getElementById('quick-add-dialog');
        if (dlg && this.closest('#quick-add-modal-content') && typeof dlg.closeDialog === 'function') {
          dlg.closeDialog();
        }
        if (!td.isOpen) td.open();
      })
      .catch(() => { location.href = '/cart'; });
  }

  add() {
    let ids = this.chosen(); if (!ids.length) return;
    if (this.myst && this.myst.checked) ids = ids.concat([this.myst.value]);
    const lab = this.querySelector('.hl-atc__label');
    const was = lab ? lab.textContent : 'Add to cart';
    this.btn.disabled = true;
    if (this.errEl) this.errEl.hidden = true;
    if (lab) lab.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) })
    })
      .then((r) => { if (!r.ok) throw 0; return r.json(); })
      .then(() => this.openDrawer())
      .catch(() => {
        this.btn.disabled = false;
        if (lab) lab.textContent = was;
        if (this.errEl) { this.errEl.textContent = 'Could not add to cart. Please try again.'; this.errEl.hidden = false; }
      });
  }
}

if (!customElements.get('hl-bundle')) customElements.define('hl-bundle', HlBundle);
